import { getModelGroupImages } from 'apis/dashboardApis'
import { searchAvailableInventory } from 'apis/vehicleApis'
import {
  AVAILABLE_INVENTORY_HEADING,
  AVAILABLE_INVENTORY_INIT_QUERY,
  AVAILABLE_INVENTORY_MAPPER,
  GENERIC_NO_VEHICLE_AVAILABLE_MESSAGE,
  SessionStorageKey
} from 'common/constants'
import {
  isAnyChilrenFiltersSelected,
  processVehicleSearchDocumentResult,
  resetChildrenFilters,
  selectChildrenFilter
} from 'common/helpers'
import Collapse from 'components/Collapse'
import {
  StyledHomeSection,
  StyledHomeSectionBody,
  StyledHomeSectionHeaderActionSection,
  StyledHomeSectionHeaderLink,
  StyledRowInventory
} from 'components/Header/HomeSectionHeader.styled'
import { ModelFigure } from 'components/ModelFigure/ModelFigure'
import { useExpand } from 'hooks/useExpand'
import { useFetch } from 'hooks/useFetch'
import _, { chain, cloneDeep, isEmpty, keyBy, merge, values } from 'lodash'
import { StyledCountingText } from 'modules/AvailableInventory.styled'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { ISearchFilterResult, IVehicleSearchDocument } from 'types/vehicleTypes'
import { HomeSectionHeader } from '../components/Header/HomeSectionHeader'
import { faCar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isReactRoutes } from 'common/routes'
import { Rules } from 'common/rules'
import { srpFilters } from 'common/srpFilters'
import { Loader } from 'components/Loader'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'
import { useHistory } from 'react-router-dom'
import { useDashboardStore } from 'store/useDashboardStore'
import { DtmComponentName, getInventoryClickName } from 'utils/analyticsUtils'
import { resetSRPFilter } from 'utils/menuUtils'
import { findModelGroupImage, getSeriesBasedOnModelGroup } from 'utils/modelGroupUtils'
import { getSearchUrl } from 'utils/urlUtils'
import shallow from 'zustand/shallow'

interface IFiltersReducerAction {
  type: 'INIT' | 'UPDATE' | 'RESET_ALL'
  payload?: ISearchFilterResult[]
}

const filterEmptyResultSearchFilters = (filters: Record<string, string[]>, ContentData: IVehicleSearchDocument[]) => {
  Object.entries(filters).forEach(([key, values]) => {
    filters[key] = [
      ...values.filter((v) => {
        // 1. Get original value (if have)
        const splittedValues = (AVAILABLE_INVENTORY_MAPPER.find((m) => m.DisplayText === v)?.Value ?? v).split(',')

        // 2. Filters from ContentData to check if we have any items

        return splittedValues.some((value) =>
          ContentData.some((data) => data[key] === value || (value === 'INFINITI' && _.upperCase(data[key]) === value))
        )
      })
    ]
  })

  return filters
}

const filtersReducer = (state: ISearchFilterResult[], action: IFiltersReducerAction): ISearchFilterResult[] => {
  switch (action.type) {
    case 'INIT':
      const initFilters = cloneDeep([...action.payload]) as ISearchFilterResult[]
      return initFilters
    case 'UPDATE':
      return values(merge(keyBy(state, 'DBFieldName'), keyBy(action.payload, 'DBFieldName')))
    case 'RESET_ALL':
      resetChildrenFilters(state)
      return state
    default:
      return state
  }
}

interface IProps {
  isFilterLoading: boolean
}

export const AvailableInventory = ({ isFilterLoading: HeadingLoading }: IProps) => {
  const { state: expandState, action: expandAction } = useExpand({ componentOpen: true } as never)
  const [displayFilters, setDisplayFilters] = useState(() => AVAILABLE_INVENTORY_HEADING)
  const [isViewAllClicked, setIsViewAllClicked] = useState(true)
  const [filters, dispatchFilters] = useReducer(filtersReducer, null)
  const [currentFuelType, setCurrentFuelType] = useState('')
  const [currentBodyStyle, setCurrentBodyStyle] = useState<string>(null)
  const history = useHistory()

  const { data: modelGroupImages = [] } = useFetch(() => getModelGroupImages())

  const { filters: HeadingData, totalVehicles: TotalRecords } = useDashboardStore((state) => state, shallow)

  const { loading: ContentLoading, data: ContentData } = useFetch(
    () => searchAvailableInventory({ ...AVAILABLE_INVENTORY_INIT_QUERY }),
    [Rules.PAGE_RDP_SRP, Rules.VIEW_ADVANCED_SEARCH_PAGE]
  )

  const { componentOpen } = expandState
  const { setExpand } = expandAction
  const { userInteraction, endComponentLoad } = useDtmAnalytics()

  useEffect(() => {
    if (!ContentLoading) {
      endComponentLoad(DtmComponentName.DashboardInventory)
    }
  }, [ContentLoading, endComponentLoad])

  // Set default filters
  useEffect(() => {
    if (HeadingLoading || !HeadingData) return

    const payload = [...HeadingData] as ISearchFilterResult[]

    dispatchFilters({ type: 'INIT', payload: payload })
  }, [HeadingLoading, HeadingData])

  // Use to update filters show on UI
  useEffect(() => {
    if (ContentLoading || !ContentData) return

    setDisplayFilters((ft) => ({ ...filterEmptyResultSearchFilters(ft, ContentData) }))
  }, [ContentLoading, ContentData])

  const makeFilters = useMemo(() => filters?.find((item) => item.FieldName === srpFilters.Make), [filters])

  const modelGroupFilters = useMemo(() => {
    if (!filters || !ContentData) return

    let result = [...ContentData]

    // Filters for Available Inventory View

    // 1. Check 'Body Style' filter
    result = processVehicleSearchDocumentResult(result, filters, srpFilters.BodyStyle, (r) => r.BodyStyle)

    // 2. Check 'Make' filter
    result = processVehicleSearchDocumentResult(result, filters, srpFilters.Make, (r) => r.Make)

    // 3. Check 'Fuel Type' filter
    if (currentFuelType !== '') {
      if (isAnyChilrenFiltersSelected(filters, srpFilters.FuelType)) {
        result = processVehicleSearchDocumentResult(result, filters, srpFilters.FuelType, (r) => r.FuelType)
      } else {
        // Remove all results as we don't have any filters match selected fuel type
        // This will make the list empty and the UI will render 'No vehicle available' message
        result = []
      }
    }

    // 4. Transform & Return result
    return Object.entries(chain(result).orderBy('ModelGroup').groupBy('ModelGroup').value())
  }, [ContentData, currentFuelType, filters])

  const handleChangeBodyStyleFilters = (bodyStyle: string) => {
    // Reset flag for Fuel Type
    setCurrentFuelType('')
    setIsViewAllClicked(false)
    resetChildrenFilters(filters)

    const originalValues = AVAILABLE_INVENTORY_MAPPER.find((m) => m.DisplayText === bodyStyle)?.Value?.split(',')
    if (
      originalValues &&
      originalValues.some((value) =>
        filters.find((q) => q.FieldName === srpFilters.BodyStyle)?.ChildrenFilter?.some((cft) => cft.Value === value)
      )
    ) {
      selectChildrenFilter(filters, srpFilters.BodyStyle, ...originalValues)
    } else {
      selectChildrenFilter(filters, srpFilters.BodyStyle, bodyStyle)
    }

    selectChildrenFilter(filters, srpFilters.BodyStyle, bodyStyle)
    setCurrentBodyStyle(bodyStyle)

    dispatchFilters({ type: 'UPDATE', payload: filters })
    userInteraction(`Available Inventory: ${bodyStyle}`)
  }

  const handleChangeFuelTypeFilters = (fuelType: string) => {
    // 0. Reset values
    resetChildrenFilters(filters)
    setCurrentBodyStyle(null)
    setIsViewAllClicked(false)

    // 1. Convert value to original value (using MAPPER)
    const originalFuelTypes = AVAILABLE_INVENTORY_MAPPER.find((m) => m.DisplayText === fuelType)?.Value?.split(',')

    // 2. If Update query with selected fuel types
    if (
      originalFuelTypes &&
      originalFuelTypes.some((value) =>
        filters.find((q) => q.FieldName === srpFilters.FuelType)?.ChildrenFilter?.some((cft) => cft.Value === value)
      )
    ) {
      selectChildrenFilter(filters, srpFilters.FuelType, ...originalFuelTypes)
    } else {
      selectChildrenFilter(filters, srpFilters.FuelType, fuelType)
    }

    dispatchFilters({ type: 'UPDATE', payload: filters })

    // Set Fuel Type flag
    setCurrentFuelType(fuelType)
    userInteraction(`Available Inventory: ${fuelType}`)
  }

  const handleChangeMakeFilters = (make: string) => {
    // Reset flag for Fuel Type
    setCurrentFuelType('')
    setCurrentBodyStyle(null)
    setIsViewAllClicked(false)
    resetChildrenFilters(filters)
    selectChildrenFilter(filters, srpFilters.Make, make)

    dispatchFilters({ type: 'UPDATE', payload: filters })
  }

  const handleRedirect = (ft?: ISearchFilterResult[]) => {
    resetSRPFilter()
    sessionStorage.setItem(SessionStorageKey.SEARCH_FILTERS, ft ? JSON.stringify(ft) : null)
    const customClickName = `Dashboard:${getInventoryClickName(ft)}`
    userInteraction(customClickName)

    if (isReactRoutes(getSearchUrl())) history.push(getSearchUrl(), ft)
    else window.location.href = getSearchUrl()
  }

  const handleFilterViewAllVehicles = () => {
    setIsViewAllClicked(true)
    setCurrentBodyStyle(null)
    setCurrentFuelType('') // Reset flag for Fuel Type
    const payload = [...HeadingData] as ISearchFilterResult[]
    dispatchFilters({ type: 'INIT', payload: payload })
  }

  const handleViewAllVehicles = () => {
    handleRedirect(null)
  }

  const handleRedirectModelGroup = (modelType: string) => {
    resetChildrenFilters(filters, srpFilters.Model)
    selectChildrenFilter(filters, srpFilters.Model, ...getSeriesBasedOnModelGroup(modelGroupFilters, modelType))

    handleRedirect(filters)
  }

  const handleRedirectYearAndModelGroup = (modelYear: string, modelType: string) => {
    resetChildrenFilters(filters, srpFilters.Model, srpFilters.Year)
    selectChildrenFilter(filters, srpFilters.Model, ...getSeriesBasedOnModelGroup(modelGroupFilters, modelType))
    selectChildrenFilter(filters, srpFilters.Year, modelYear)

    handleRedirect(filters)
  }

  return (
    <StyledHomeSection>
      <HomeSectionHeader
        sectionTitle="Available Inventory"
        onClick={() => {
          setExpand(!componentOpen)
          userInteraction(`Available Inventory: ${componentOpen ? 'Collapse' : 'Expand'}`)
        }}
        isExpand={componentOpen}
        leftActions={
          !ContentLoading &&
          !HeadingLoading &&
          ContentData?.length !== 0 && (
            <>
              <StyledHomeSectionHeaderActionSection className="d-flex">
                <StyledHomeSectionHeaderLink
                  onClick={() => handleFilterViewAllVehicles()}
                  active={isViewAllClicked}
                  style={{ marginRight: '2rem' }}
                >
                  <span>View All</span>
                </StyledHomeSectionHeaderLink>
                {displayFilters.BodyStyle.map((bodyStyle, idx) => {
                  return (
                    <StyledHomeSectionHeaderLink
                      key={idx}
                      onClick={handleChangeBodyStyleFilters.bind(null, bodyStyle)}
                      active={currentBodyStyle === bodyStyle}
                    >
                      <span>{bodyStyle}</span>
                    </StyledHomeSectionHeaderLink>
                  )
                })}
              </StyledHomeSectionHeaderActionSection>
              <StyledHomeSectionHeaderActionSection className="d-flex">
                {displayFilters.FuelType.map((type, index) => (
                  <StyledHomeSectionHeaderLink
                    key={index}
                    onClick={handleChangeFuelTypeFilters.bind(null, type)}
                    active={currentFuelType === type}
                  >
                    <span>{type}</span>
                  </StyledHomeSectionHeaderLink>
                ))}
              </StyledHomeSectionHeaderActionSection>
              <StyledHomeSectionHeaderActionSection className="d-flex">
                {displayFilters.Make.map((type, index) => (
                  <StyledHomeSectionHeaderLink
                    key={index}
                    onClick={handleChangeMakeFilters.bind(null, type)}
                    active={makeFilters?.ChildrenFilter?.find((cf) => cf.Value === type)?.Selected}
                  >
                    <span>{type === 'Infiniti' ? 'INFINITI' : type}</span>
                  </StyledHomeSectionHeaderLink>
                ))}
              </StyledHomeSectionHeaderActionSection>
            </>
          )
        }
        rightActions={
          !(HeadingLoading || ContentLoading) && (
            <StyledHomeSectionHeaderActionSection className="d-flex">
              <StyledHomeSectionHeaderLink onClick={handleViewAllVehicles}>
                View All Vehicles&nbsp;
                <StyledCountingText>({TotalRecords ?? 0})</StyledCountingText>
              </StyledHomeSectionHeaderLink>
            </StyledHomeSectionHeaderActionSection>
          )
        }
      />
      <Collapse in={componentOpen}>
        <StyledHomeSectionBody>
          {(ContentLoading || HeadingLoading) && <Loader overlay />}
          {!ContentLoading && !HeadingLoading && isEmpty(modelGroupFilters) && (
            <Row>
              <Col className="d-flex flex-column justify-content-center align-items-center py-5 text-secondary">
                <FontAwesomeIcon icon={faCar} className="fa-4x" />
                <div>{GENERIC_NO_VEHICLE_AVAILABLE_MESSAGE}</div>
              </Col>
            </Row>
          )}

          {!ContentLoading && !HeadingLoading && !isEmpty(modelGroupFilters) && (
            <StyledRowInventory>
              {modelGroupFilters
                ?.filter(([mg, _]) => mg !== 'undefined')
                .map(([modelGroup, arrOfSeries]) => (
                  <ModelFigure
                    key={`${modelGroup}_${arrOfSeries}`}
                    imageUrl={findModelGroupImage(modelGroupImages, modelGroup, currentBodyStyle, currentFuelType)}
                    modelType={modelGroup}
                    make={arrOfSeries[0].Make}
                    modelYears={Object.entries(
                      chain(arrOfSeries).orderBy('ModelYear').countBy('ModelYear').value()
                    ).map(([year, count]) => ({ year, total: count }))}
                    total={arrOfSeries?.length}
                    onRedirect={handleRedirectModelGroup.bind(null, modelGroup)}
                    handleRedirectYearAndModel={handleRedirectYearAndModelGroup}
                  />
                ))}
            </StyledRowInventory>
          )}
        </StyledHomeSectionBody>
      </Collapse>
    </StyledHomeSection>
  )
}
